import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO } from '../../hooks/useSEO';
import { SITE_URL } from '../../utils/siteConfig';

const steps = [
  { icon: 'ri-message-3-line', step: '01', title: 'Stuur uw product door', desc: 'Beschrijf uw product of stuur een foto/link. Wij nemen het vanaf hier over.' },
  { icon: 'ri-search-2-line', step: '02', title: 'Wij zoeken de beste fabriek', desc: 'Ons netwerk van 2.000+ fabrikanten — wij selecteren de juiste leverancier voor uw product en budget.' },
  { icon: 'ri-file-list-3-line', step: '03', title: 'Gratis prijsindicatie binnen 24u', desc: 'U ontvangt een transparante, all-in offerte. Geen verborgen kosten, geen verplichtingen.' },
  { icon: 'ri-truck-line', step: '04', title: 'Productie & DDP levering', desc: 'Na akkoord regelen wij alles: productie, kwaliteitscontrole en DDP levering aan uw deur.' },
];

const voordelen = [
  { icon: 'ri-money-euro-circle-line', title: '30–80% goedkoper', desc: 'Direct bij de fabriek inkopen zonder tussenhandel. Geen importeur, geen distributeur.' },
  { icon: 'ri-shield-check-line', title: 'Kwaliteitscontrole', desc: 'Steekproefsgewijze inspecties voor en na productie. Alleen gecertificeerde fabrieken.' },
  { icon: 'ri-truck-line', title: 'All-in DDP levering', desc: 'Transport, invoerrechten en BTW inbegrepen. Eén bedrag, aan uw deur bezorgd.' },
  { icon: 'ri-time-line', title: 'Reactie binnen 24 uur', desc: 'Gratis en vrijblijvende prijsindicatie. Snel, duidelijk en professioneel.' },
  { icon: 'ri-paint-brush-line', title: 'Private label mogelijk', desc: 'Uw eigen logo en branding op producten en verpakkingen. OEM & ODM productie.' },
  { icon: 'ri-team-line', title: 'Volledige begeleiding', desc: 'Van eerste contact tot levering regelen wij alles. U hoeft zich nergens zorgen om te maken.' },
];

const sectoren = [
  { label: 'Horeca', icon: 'ri-restaurant-line', href: '/voor-wie/horeca' },
  { label: 'Webshops', icon: 'ri-shopping-cart-line', href: '/voor-wie/webshops' },
  { label: 'Beauty & Wellness', icon: 'ri-scissors-cut-line', href: '/voor-wie/beauty' },
  { label: 'Retail', icon: 'ri-store-line', href: '/voor-wie/retail' },
  { label: 'Juweliers', icon: 'ri-vip-diamond-line', href: '/voor-wie/juweliers' },
  { label: 'Events & Promotie', icon: 'ri-megaphone-line', href: '/voor-wie/evenementen' },
  { label: 'Bedrijven & B2B', icon: 'ri-building-line', href: '/voor-wie/bedrijven' },
  { label: 'Overig', icon: 'ri-more-line', href: '/voor-wie/overig' },
];

const faqs = [
  { q: 'Hoe werkt inkopen uit China via DUC?', a: 'U stuurt ons uw product of beschrijving. Wij zoeken de beste fabriek, sturen binnen 24 uur een gratis prijsindicatie en regelen de volledige productie en DDP levering naar uw deur in Nederland.' },
  { q: 'Hoeveel kan ik besparen door goedkoper in te kopen uit China?', a: 'Klanten besparen gemiddeld 30–80% ten opzichte van Nederlandse leveranciers. Direct bij de fabrikant inkopen zonder tussenhandel maakt dit mogelijk.' },
  { q: 'Wat zijn de risicos van inkopen bij fabrikanten in China?', a: 'DUC minimaliseert risicos door alleen met geverifieerde fabrieken te werken, kwaliteitscontroles uit te voeren en samplebeoordeling aan te raden. Wij begeleiden het hele traject.' },
  { q: 'Wat is de minimale bestelhoeveelheid voor import uit China?', a: 'De MOQ varieert per product, maar voor veel producten beginnen we al vanaf 50 stuks. Wij zoeken altijd een fabriek die past bij uw volume.' },
  { q: 'Hoe lang duurt levering uit China naar Nederland?', a: 'Via zeecontainer 8–16 weken (inclusief productie). Via luchtvervoer 1–3 weken. Wij geven altijd een realistische levertijdschatting.' },
  { q: 'Werkt DUC ook met kleine bedrijven en particulieren?', a: 'Ja. Wij werken met zowel grote B2B-klanten als kleine webshops, horecazaken en particulieren. Iedereen kan profiteren van goedkoper inkopen uit China.' },
];

function FaqItem({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-xl border transition-all duration-200 overflow-hidden ${open ? 'border-[#3B9EE8]/40 bg-white' : 'border-[#0A1628]/8 bg-white hover:border-[#3B9EE8]/25'}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-7 py-5 text-left cursor-pointer">
        <span className={`font-500 text-base pr-8 leading-snug ${open ? 'text-[#0A1628] font-600' : 'text-[#0A1628]/80'}`}>{q}</span>
        <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-200 ${open ? 'bg-[#3B9EE8] rotate-45' : 'bg-[#0A1628]/6'}`}>
          <i className={`ri-add-line text-sm ${open ? 'text-white' : 'text-[#0A1628]/60'}`} />
        </div>
      </button>
      {open && (
        <div className="px-7 pb-6 border-t border-[#3B9EE8]/15">
          <p className="text-[#0A1628]/65 text-base leading-relaxed pt-4">{a}</p>
        </div>
      )}
    </div>
  );
}

export default function InkopenUitChinaPage() {
  useSEO({
    title: 'Inkopen uit China | Goedkoop Importeren bij Fabrikanten | DUC Sourcing Agent',
    description: 'Inkopen uit China via sourcing agent DUC. Bespaar 30–80% door direct bij fabrikanten in te kopen. Product sourcing, kwaliteitscontrole & all-in DDP levering. Gratis offerte binnen 24 uur.',
    keywords: 'inkopen uit China, goedkoop inkopen fabrikanten China, sourcing agent China, import China Nederland, DDP levering, product sourcing China, leverancier vinden China',
    canonical: `${SITE_URL}/inkopen-uit-china`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Inkopen uit China', item: `${SITE_URL}/inkopen-uit-china` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Inkopen uit China — 30–80% goedkoper via sourcing agent DUC',
        description: 'Direct Uit China (DUC) helpt bedrijven en particulieren goedkoper inkopen bij fabrikanten in China. Product sourcing, kwaliteitscontrole en DDP levering.',
        url: `${SITE_URL}/inkopen-uit-china`,
        inLanguage: 'nl-NL',
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
            { '@type': 'ListItem', position: 2, name: 'Inkopen uit China', item: `${SITE_URL}/inkopen-uit-china` },
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Hoe werkt inkopen uit China via DUC?',
            acceptedAnswer: { '@type': 'Answer', text: 'U stuurt ons uw product of beschrijving. Wij zoeken de beste fabriek, sturen binnen 24 uur een gratis prijsindicatie en regelen de volledige productie en DDP levering naar uw deur in Nederland.' },
          },
          {
            '@type': 'Question',
            name: 'Hoeveel kan ik besparen door goedkoper in te kopen uit China?',
            acceptedAnswer: { '@type': 'Answer', text: 'Klanten besparen gemiddeld 30–80% ten opzichte van Nederlandse leveranciers. Direct bij de fabrikant inkopen zonder tussenhandel maakt dit mogelijk.' },
          },
          {
            '@type': 'Question',
            name: 'Wat is de minimale bestelhoeveelheid voor import uit China?',
            acceptedAnswer: { '@type': 'Answer', text: 'De MOQ varieert per product, maar voor veel producten beginnen we al vanaf 50 stuks. Wij zoeken altijd een fabriek die past bij uw volume.' },
          },
          {
            '@type': 'Question',
            name: 'Hoe lang duurt levering uit China naar Nederland?',
            acceptedAnswer: { '@type': 'Answer', text: 'Via zeecontainer 8–16 weken (inclusief productie). Via luchtvervoer 1–3 weken. Wij geven altijd een realistische levertijdschatting.' },
          },
          {
            '@type': 'Question',
            name: 'Werkt DUC ook met kleine bedrijven en particulieren?',
            acceptedAnswer: { '@type': 'Answer', text: 'Ja. Wij werken met zowel grote B2B-klanten als kleine webshops, horecazaken en particulieren. Iedereen kan profiteren van goedkoper inkopen uit China.' },
          },
        ],
      },
    ],
  });

  const heroRef = useRef<HTMLDivElement>(null);
  const heroInView = useInView(heroRef, { once: true });
  const stepsRef = useRef<HTMLDivElement>(null);
  const stepsInView = useInView(stepsRef, { once: true, margin: '-80px' });
  const voordelenRef = useRef<HTMLDivElement>(null);
  const voordelenInView = useInView(voordelenRef, { once: true, margin: '-80px' });

  return (
    <main className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section ref={heroRef} className="relative bg-[#0A1628] pt-[100px] sm:pt-[116px] lg:pt-[136px] pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/959b4fc99d24150a846e62c42f74ea9b.jpg"
            alt="Inkopen uit China — Direct bij fabrikanten via sourcing agent DUC"
            className="w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/90 via-[#0A1628]/70 to-[#0A1628]/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }}>
            <Link to="/" className="inline-flex items-center gap-2 text-white/45 hover:text-[#3B9EE8] text-sm mb-8 transition-colors cursor-pointer">
              <i className="ri-arrow-left-line" />
              Terug naar home
            </Link>
          </motion.div>
          <motion.span initial={{ opacity: 0, y: 16 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.1 }} className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-600 mb-6">
            <i className="ri-global-line" />
            Sourcing Agent China &bull; Nederland
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.15 }} className="font-heading font-800 text-white text-4xl md:text-5xl lg:text-6xl leading-tight mb-6 max-w-3xl">
            Inkopen uit China —<br />
            <span className="text-[#3B9EE8]">30–80% goedkoper</span><br />
            dan uw huidige leverancier
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.25 }} className="text-white/60 text-lg max-w-2xl leading-relaxed mb-8">
            Direct Uit China (DUC) is uw <strong className="text-white/85">sourcing agent</strong> voor goedkoop inkopen bij fabrikanten in China. Wij regelen <strong className="text-white/85">product sourcing</strong>, kwaliteitscontrole en <strong className="text-white/85">DDP levering</strong> aan uw deur — compleet en transparant.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.35 }} className="flex flex-wrap gap-4">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-[#3B9EE8] hover:bg-[#2D85C5] text-white px-7 py-3.5 rounded-lg font-700 text-sm transition-all cursor-pointer whitespace-nowrap">
              Start met goedkoper inkopen
              <i className="ri-arrow-right-line" />
            </Link>
            <a href="https://wa.me/31624359162?text=Hi%2C%20ik%20wil%20graag%20weten%20hoe%20ik%20goedkoper%20kan%20inkopen%20uit%20China." target="_blank" rel="nofollow noreferrer" className="inline-flex items-center gap-2 border border-white/25 hover:border-[#3B9EE8]/60 text-white hover:text-[#3B9EE8] px-6 py-3.5 rounded-lg font-500 text-sm transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-whatsapp-line text-base" />
              WhatsApp ons
            </a>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.45 }} className="flex flex-wrap gap-6 pt-8 mt-8 border-t border-white/10">
            {[{ v: '30–80%', l: 'Besparing op inkoop' }, { v: '24u', l: 'Gratis prijscheck' }, { v: 'DDP', l: 'All-in levering' }, { v: '100%', l: 'Transparant' }].map((s) => (
              <div key={s.v} className="flex flex-col">
                <span className="font-heading font-800 text-white text-xl">{s.v}</span>
                <span className="text-white/40 text-xs mt-0.5">{s.l}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Wat is inkopen uit China */}
      <section className="py-20 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <span className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-600 mb-6">
                <i className="ri-question-line" />
                Waarom inkopen uit China?
              </span>
              <h2 className="font-heading font-800 text-[#0A1628] text-3xl md:text-4xl leading-tight mb-6">
                Goedkoper inkopen voor uw bedrijf —<br />
                <strong className="text-[#3B9EE8]">direct bij de fabriek</strong>
              </h2>
              <p className="text-[#0A1628]/65 text-base leading-relaxed mb-5">
                China is de grootste producent ter wereld. Door <strong>direct bij fabrikanten in te kopen</strong> in plaats van via importeurs en distributeurs, betaalt u tot 80% minder voor dezelfde producten. Het probleem? Taal, afstand en kwaliteitsrisico&apos;s maken dit complex.
              </p>
              <p className="text-[#0A1628]/65 text-base leading-relaxed mb-7">
                DUC is uw <strong>sourcing agent China</strong> — wij overbruggen die kloof. Wij spreken de taal, kennen de fabrieken, bewaken de kwaliteit en regelen de <strong>import uit China</strong> volledig voor u. U hoeft niets te weten van douane, productiebegeleiding of logistiek.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: 'ri-building-line', text: '2.000+ geverifieerde fabrieken' },
                  { icon: 'ri-time-line', text: 'Offerte binnen 24 uur' },
                  { icon: 'ri-global-line', text: 'Direct communicatie met leverancier' },
                  { icon: 'ri-shield-check-line', text: 'Kwaliteitscontrole inbegrepen' },
                ].map((i) => (
                  <div key={i.text} className="flex items-center gap-3 bg-white rounded-xl p-4 border border-[#0A1628]/6">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#3B9EE8]/12 flex-shrink-0">
                      <i className={`${i.icon} text-[#3B9EE8] text-sm`} />
                    </div>
                    <span className="text-[#0A1628]/75 text-xs font-500 leading-snug">{i.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative h-96 lg:h-[480px] rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20business%20meeting%20handshake%20import%20export%20China%20Netherlands%20trading%20partnership%20supply%20chain%20modern%20bright%20office%20clean%20minimal%20professional%20photography%20navy%20blue%20tones&width=700&height=480&seq=inkopen-china-why-001&orientation=landscape"
                alt="Sourcing agent China — goedkoper inkopen bij fabrikanten"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#0A1628]/80 to-transparent">
                <p className="text-white font-600 text-sm">DUC regelt alles — van sourcing tot levering</p>
                <p className="text-white/55 text-xs mt-1">Product sourcing &bull; Kwaliteitscontrole &bull; DDP import</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hoe het werkt */}
      <section ref={stepsRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-600 mb-6">
              <i className="ri-route-line" />
              Werkwijze
            </span>
            <h2 className="font-heading font-800 text-[#0A1628] text-3xl md:text-4xl">
              Hoe werkt inkopen uit China <span className="text-[#3B9EE8]">via DUC?</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <motion.div key={s.step} initial={{ opacity: 0, y: 24 }} animate={stepsInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: i * 0.1 }} className="bg-[#F7F8FA] rounded-2xl p-7 border border-[#0A1628]/5 relative">
                <span className="absolute top-5 right-6 font-heading font-900 text-[#0A1628]/6 text-5xl leading-none pointer-events-none select-none">{s.step}</span>
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#3B9EE8]/12 mb-5">
                  <i className={`${s.icon} text-[#3B9EE8] text-xl`} />
                </div>
                <h3 className="font-heading font-700 text-[#0A1628] text-base mb-2">{s.title}</h3>
                <p className="text-[#0A1628]/55 text-sm leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/hoe-het-werkt" className="inline-flex items-center gap-2 text-[#3B9EE8] hover:text-[#2D85C5] font-600 text-sm transition-colors cursor-pointer whitespace-nowrap">
              Bekijk het volledige proces
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* Voordelen */}
      <section ref={voordelenRef} className="py-20 bg-[#0A1628]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <h2 className="font-heading font-800 text-white text-3xl md:text-4xl mb-4">
              Voordelen van <span className="text-[#3B9EE8]">goedkoop inkopen uit China</span><br />via DUC
            </h2>
            <p className="text-white/50 text-base max-w-xl mx-auto">Waarom kiezen honderden bedrijven voor DUC als hun sourcing agent China?</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {voordelen.map((v, i) => (
              <motion.div key={v.title} initial={{ opacity: 0, y: 20 }} animate={voordelenInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: i * 0.08 }} className="bg-white/5 border border-white/8 rounded-xl p-6">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#3B9EE8]/15 mb-4">
                  <i className={`${v.icon} text-[#3B9EE8] text-lg`} />
                </div>
                <h3 className="font-heading font-700 text-white text-base mb-2">{v.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sectoren */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="font-heading font-800 text-[#0A1628] text-3xl md:text-4xl mb-4">
              Voor wie is <span className="text-[#3B9EE8]">inkopen uit China</span> geschikt?
            </h2>
            <p className="text-[#0A1628]/55 text-base max-w-xl mx-auto">Van horeca tot webshops — wij helpen elk type bedrijf met goedkoper inkopen.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {sectoren.map((s) => (
              <Link key={s.label} to={s.href} className="flex flex-col items-center gap-3 bg-[#F7F8FA] border border-[#0A1628]/6 rounded-xl p-6 hover:border-[#3B9EE8]/40 hover:bg-[#3B9EE8]/5 transition-all cursor-pointer group">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#3B9EE8]/12 group-hover:bg-[#3B9EE8]/20 transition-colors">
                  <i className={`${s.icon} text-[#3B9EE8] text-lg`} />
                </div>
                <span className="text-[#0A1628]/75 text-sm font-600 text-center">{s.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-[#F7F8FA]">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="font-heading font-800 text-[#0A1628] text-3xl md:text-4xl mb-3">
              Veelgestelde vragen over <span className="text-[#3B9EE8]">inkopen uit China</span>
            </h2>
            <p className="text-[#0A1628]/55 text-base">Antwoorden op de meest gestelde vragen over goedkoper inkopen, import en sourcing.</p>
          </div>
          <div className="space-y-2">
            {faqs.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} index={i} />)}
          </div>
          <div className="text-center mt-8">
            <Link to="/faq" className="inline-flex items-center gap-2 text-[#3B9EE8] font-600 text-sm transition-colors cursor-pointer whitespace-nowrap hover:text-[#2D85C5]">
              Bekijk alle veelgestelde vragen
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0A1628]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading font-800 text-white text-3xl md:text-4xl mb-4">
            Klaar om <span className="text-[#3B9EE8]">goedkoper in te kopen?</span>
          </h2>
          <p className="text-white/55 text-base mb-8 leading-relaxed max-w-xl mx-auto">
            Vraag een gratis en vrijblijvende prijsindicatie aan. Binnen 24 uur weet u hoeveel u kunt besparen op uw inkoop uit China.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-[#3B9EE8] hover:bg-[#2D85C5] text-white px-8 py-4 rounded-lg font-700 transition-all cursor-pointer whitespace-nowrap">
              Vraag gratis prijsindicatie aan
              <i className="ri-arrow-right-line" />
            </Link>
            <a href="https://wa.me/31624359162?text=Hi%2C%20ik%20wil%20graag%20goedkoper%20inkopen%20uit%20China." target="_blank" rel="nofollow noreferrer" className="inline-flex items-center gap-2 border border-white/25 hover:border-[#3B9EE8]/60 text-white hover:text-[#3B9EE8] px-7 py-4 rounded-lg font-600 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-whatsapp-line text-xl" />
              WhatsApp direct
            </a>
          </div>
          <div className="mt-10 pt-8 border-t border-white/10 flex flex-wrap justify-center gap-x-8 gap-y-3">
            {[
              { icon: 'ri-check-line', text: 'Gratis & vrijblijvend' },
              { icon: 'ri-time-line', text: 'Reactie binnen 24 uur' },
              { icon: 'ri-shield-check-line', text: 'Geen verplichtingen' },
              { icon: 'ri-links-line', text: 'Interne links naar diensten' },
            ].map((i) => (
              <div key={i.text} className="flex items-center gap-2">
                <i className={`${i.icon} text-[#3B9EE8] text-sm`} />
                <span className="text-white/50 text-sm">{i.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
